<template>
  <div  v-if="!hasDetails" :style="`height: 70vh; position: relative; text-align: center; color: ${order.color || 'red'}`">
    <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%)">
      <div>
        <i :class="(order.icon || 'fas fa-times') + ' fa-10x'"></i>
      </div>
      <div style="font-size: 200%; margin-top: 20px">
        {{ $t( order.message || 'Orders.noOrders' )}}
      </div>
    </div>
  </div>
  <div v-else ui-view>
    <!-- Orderstatus mit Tracking-Code und Rechnungslink -->
    <order-messages
      :transactionnum="transactionnum"
      :order-id="orderId"
      ref="messages"
    ></order-messages>
    <div class="row" v-if="transactionnum">
      <div class="col-md-12">
        <steps v-if="orderStatuses">
          <template v-for="step in orderStatusBar">
            <step
                    :step="step.name"
                    :active="step.active"
                    :title="$root.getLabelText(step.title)"
                    :description="$root.getLabelText(step.description)"
                    :icon="step.icon"
            >
              <card v-if="step.name === 'inprogress' && hasStatusLabel" :title="$t('OrderDetails.manufacturingStatus')">
                <label>{{ $t('Common.status') }}:</label> {{ $t(statusLabel) }}
              </card>
              <card v-else-if="step.name === 'delivered' && deliveries && deliveries.length" :title="$t('OrderDetails.trackingAndBilling')">
                <div v-for="(delivery, index) in deliveries" v-if="deliveries">
                  {{ $t('OrderDetails.deliveryNote') }}
                  <a v-if="allowDeliveryPrint" :href="printUrl(delivery)" target="_blank">{{ delivery.transactionnum }}</a>
                  <strong v-else>{{ delivery.transactionnum }}</strong>
                  , {{ delivery.date | formatDate }}
                  <template v-if="delivery.extdata.trackingnum">
                    <label>{{ $t('OrderDetails.trackingNum') }}: </label>
                    <a v-if="delivery.extdata.trackinglink" :href="delivery.extdata.trackinglink" target="_blank">{{ delivery.extdata.trackingnum }}</a>
                    <span v-else>{{ delivery.extdata.trackingnum }}</span>
                  </template>
                </div>
              </card>
            </step>
          </template>
        </steps>
        <steps v-else>
          <step :active="stepInbox" step="received" :title="$t('Orders.statusReceived')" :description="$t('Orders.statusReceivedDescr')" icon="inbox"></step>
          <step :active="stepInProgress" step="inprogress" :title="$t('Orders.statusInProgress')" :description="$t('Orders.statusInProgressDescr')" icon="thumbs-up"></step>
          <step :active="stepCancelled" step="cancelled" v-if="stepCancelled" :title="$t('Orders.statusCancelled')" step-class="cancelled" :description="$t('Orders.statusCancelledDescr')" icon="times"></step>
          <step :active="stepInProduction" step="inproduction" v-if="!stepCancelled" :title="$t('Orders.statusInProduction')" :description="$t('Orders.statusInProductionDescr')" icon="cogs">
            <card :title="$t('OrderDetails.manufacturingStatus')" v-if="hasStatusLabel">
              <label>{{ $t('Common.status') }}:</label> {{ $t(statusLabel) }}
            </card>
          </step>
          <step :active="stepCompleted" step="completed" v-if="stepCompleted" :title="$t('Orders.statusCompleted')" :description="$t('Orders.statusCompletedDescr')" icon="check"></step>
          <step :active="stepDelivered" step="delivered" v-if="!stepCancelled && !stepCompleted" :title="$t('Orders.statusDelivered')" :description="$t('Orders.statusDeliveredDescr')" icon="truck">
            <card :title="$t('OrderDetails.trackingAndBilling')" v-if="deliveries && deliveries.length">
              <div v-for="(delivery, index) in deliveries">
                {{ $t('OrderDetails.deliveryNote') }}
                <a v-if="allowDeliveryPrint" :href="printUrl(delivery)" target="_blank">{{ delivery.transactionnum }}</a>
                <strong v-else>{{ delivery.transactionnum }}</strong>
                , {{ delivery.date | formatDate }}
                <template v-if="delivery.extdata.trackingnum">
                  <label>{{ $t('OrderDetails.trackingNum') }}: </label>
                  <a v-if="delivery.extdata.trackinglink" :href="delivery.extdata.trackinglink" target="_blank">{{ delivery.extdata.trackingnum }}</a>
                  <span v-else>{{ delivery.extdata.trackingnum }}</span>
                </template>
              </div>
            </card>
          </step>
        </steps>
      </div>
    </div>

    <div v-if="alertTitle" class="row">
      <div class="col-md-12">
        <alert
          :title="alertTitle"
          :color="alertStyle"
          :text="alertText"
          :icon="alertIcon"
          :dismissable="!alertCritical"
          v-on:dismiss="closeAlert"
        ></alert>
      </div>
    </div>

    <div class="row" v-if="order.alert">
      <div :class="order.alert.style" v-html="$t(order.alert)"></div>
    </div>

    <!-- File attachments -->
    <div class="row" v-if="hasAttachments">
      <div class="col-md-12">
        <card :title="$t('OrderDetails.files')" body-class="no-padding table-responsive">
          <table class="datatable table table-striped primary dataTable no-footer card-table">
            <thead>
              <tr>
                <th>{{ $t('File.name') }}</th>
                <th>{{ $t('Common.description') }}</th>
                <th>{{ $t('Common.creationdate') }}</th>
                <th>{{ $t('File.size') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(file, index) in attachments">
                <td>
                  <a :href="getDownloadUrl(file)" target="_blank"><span class="fileicon fileicon-sm float-left mright-10" :data-type="getFileExtension(file)" :style="{background: getIconColor(file)}"></span>{{file.name }}</a>
                </td>
                <td>{{ file.description }}</td>
                <td>{{ file.creationdate | formatDate }}</td>
                <td>{{ file.size | formatSize }}</td>
              </tr>
            </tbody>
          </table>
        </card>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <card :title="$t('ProjectDetails.cart')" :loader="activeRequest" body-class="no-padding table-responsive">
          <div class="pad-20" v-if="containsNobleMetal">
            <div class="alert alert-warning">
              <p>{{ $t('OrderDetails.priceNobleMetal1') }}</p>
              <p>{{ $t('OrderDetails.priceNobleMetal2') }}</p>
            </div>
          </div>
          <cart
            class="card-table"
            :show-prices="showPrices"
            :items="cart.items"
            :netamount="cart.netamount"
            :tax="cart.tax"
            :total="cart.total"
            :suffix-fields="suffixFields"
          ></cart>
        </card>
      </div>
    </div>
    <div class="row" v-if="transactionnum">
      <div class="col-lg-3 col-md-6 col-sm-6 col-xs-12">
        <addresscard
          v-if="shippingrecipient"
          :lastname="shippingrecipient"
          :address="shippingaddress"
          :postalcode="shippingpostalcode"
          :city="shippingcity"
          :region="shippingregion"
          :country="shippingcountry"
          :editable="false"
          :removable="false"
          :selectable="false"
          :changable="false"
          :is-shipping="1"
        ></addresscard>
      </div>
      <div class="col-lg-3 col-md-6 col-sm-6 col-xs-12">
        <addresscard
          v-if="billingrecipient"
          :lastname="billingrecipient"
          :address="billingaddress"
          :postalcode="billingpostalcode"
          :city="billingcity"
          :region="billingregion"
          :country="billingcountry"
          :editable="false"
          :removable="false"
          :selectable="false"
          :changable="false"
          :is-billing="1"
        ></addresscard>
      </div>
      <banner
        class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
        color="green"
        :title="lastMessageTitle"
        :icon="lastMessageIcon"
        v-on:click="openMessages"
        v-if="useMessages"
      >
        <div class="msgpreview">{{ lastMessagePreview }}</div>
      </banner>
      <banner
        class="col-lg-3 col-md-6 col-sm-6 col-xs-12"
        color="blue"
        :title="stlTitle"
        :suffix="stlSuffix"
        :value="stlCount"
        :icon="stlIcon"
        :image="stlImage"
        v-on:click="openProject"
      >
        <div class="msgpreview" v-if="!hasFiles">{{ $t('ProjectDetails.title') }}</div>
      </banner>
    </div>
    <div class="row" v-if="orderHistory.length > 0">
      <div class="order-history">
        <div v-for="project in orderHistory">
          <div class="order-title" @click="openProject(project.ID)">{{project.name}}</div>
          <div>
            <div class="order-date">{{project.creationdate | formatDate}}</div>
            <div>
              <project-status-badge :status="project.status"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import OrderMessages from './OrderMessages.vue';
import {met} from '../StatusBadge';
import ProjectStatusBadge from '../Elements/ProjectStatusBadge';

var util = require('../util.js'),
    view = require('../view.js'),
    viewData = {
      activeRequest: '',
      alertTitle: '',
      alertText: '',
      alertStyle: '',
      alertIcon: '',
      status: '',
      shippingaddress: '',
      shippingcity: '',
      shippingcountry: '',
      shippingpostalcode: '',
      shippingrecipient: '',
      shippingregion: '',
      billingaddress: '',
      billingcity: '',
      billingcountry: '',
      billingpostalcode: '',
      billingrecipient: '',
      billingregion: '',
      transactionnum: '',
      lastcomment: null,
      attachments: [],
      extdata: {},
      cart: {},
      deliveries: {},
      project: {},
      suffixFields: {
        color: 'Farbe'
      },
      workflow: '',
      orderHistory: [],
      order: {},
    },
    resetData = Object.assign({}, viewData);

export default view(viewData, {
  components: {
    OrderMessages,
    ProjectStatusBadge,
  },
  computed: {
    hasDetails() {
      return this.order.ID == null || this.order.transactionnum
    },
    orderId() {
      return this.$parent.viewIndex;
    },
    previousView() {
      return this.$parent.viewParam === null ? 'Orders' : this.$parent.viewParam;
    },
    showPrices() {
      return typeof this.$parent.currentUser.hidePrices == 'undefined' ? true : !this.$parent.currentUser.hidePrices;
    },
    stepInbox() {
      return this.status == 0;
    },
    stepInProgress() {
      return this.status == 1 && !this.stepInProduction;
    },
    stepInProduction() {
      return this.status == 1 && this.statusLabel;
    },
    stepDelivered() {
      return this.status >= 9;
    },
    stepCompleted() {
      return this.status == 4;
    },
    stepCancelled() {
      return this.status == 3;
    },
    statusCodes() {
      if (typeof this.$root.orderStatusCodes == 'object')
        return this.$root.orderStatusCodes;

      return {};
    },
    statusLabel() {
      var status = typeof viewData.extdata.status == 'undefined' ? 'notstarted' : viewData.extdata.status;
      return typeof this.statusCodes[status] == 'undefined' ? false : this.statusCodes[status];
    },
    hasStatusLabel() {
      var status = typeof viewData.extdata.status == 'undefined' ? 'notstarted' : viewData.extdata.status;
      return typeof this.statusCodes[status] == 'string' || (typeof this.statusCodes[status] == 'object' && this.statusCodes[status] != null);
    },
    files() {
      if ((typeof this.project.data != 'undefined') && (typeof this.project.data.files != 'undefined') && this.project.data.files.length > 0) {
        return this.project.data.files;
      };

      return [];
    },
    allowDeliveryPrint() {
      return typeof this.$root.allowDeliveryPrint == 'undefined' || this.$root.allowDeliveryPrint != false;
    },
    hasFiles() {
      return this.files.length > 0;
    },
    stlTitle() {
      return this.hasFiles ? this.$t('ProjectDetails.title') : '';
    },
    stlIcon() {
      if (!this.hasFiles)
        return 'cog';
    },
    stlImage() {
      if (this.hasFiles) {
        return this.files[0].imageUrl + '/' + this.files[0].images[0] + '.png';
      }
    },
    stlCount() {
      if (this.hasFiles)
        return this.files.length;

      return '';
    },
    stlSuffix() {
      if (this.files.length > 0) {
        return this.files.length == 1 ? 'STL' : 'STLs';
      }
    },
    lastMessageTitle() {
      if (this.lastcomment == null || typeof this.lastcomment.text == 'undefined' || this.lastcomment.text == null) {
        return '';
      }

      return (new Date(this.lastcomment.date * 1000)).toLocaleString('de-DE');
    },
    lastMessageIcon() {
      if (this.lastcomment == null || typeof this.lastcomment.text == 'undefined' || this.lastcomment.text == null) {
        return 'comment-alt';
      }

      return 'comments';
    },
    lastMessagePreview() {
      if (this.lastcomment == null || typeof this.lastcomment.text == 'undefined' || this.lastcomment.text == null) {
        return this.$t('Messages.noMessages');
      }

      if (this.lastcomment.text.length > 50)
        return this.lastcomment.text.substring(0, 47) + '...';
      else
        return this.lastcomment.text;
    },
    hasAttachments() {
      return this.attachments.length > 0;
    },
    useMessages() {
      return !this.$root.isTrue('disableMessages');
    },
    containsNobleMetal() {
      if (typeof viewData.cart.items == 'object') {
        for (let i = 0 ; i < viewData.cart.items.length ; i++) {
          if (viewData.cart.items[i] !== null && typeof viewData.cart.items[i].unit == 'string' && viewData.cart.items[i].unit == 'GRM') {
            return true;
          }
        }
      }

      return false;
    },
    orderStatuses() {
      return ((this.$root.workflowTypes || {})[this.project.type] || {}).orderStatuses;
    },
    orderStatusBar() {
      const statuses  = this.orderStatuses;
      console.log('statuses', statuses);

      const result = [];
      for(let status of statuses) {
        const active = met(status.conditions, this.order);
        if (!!status.optional && !active) {
          continue;
        }

        result.push({
          active: active,
          name: status.name,
          title: status.title,
          description: status.description,
          icon: status.icon,
        });

        if (!!status.terminate && active) {
          break;
        }
      }

      console.log('result', result);

      return result;
    }
  },
  mounted() {
    if (!this.orderId) {
      this.$parent.openView('Orders');
      this.$parent.alertDanger(this.$t('OrderDetails.errorNoOrderNum'));
    }

    // viewParam
    this.$parent.viewTitle = this.$t('OrderDetails.title');
    this.$parent.setPrevious(this.previousView);
    this.reset();
  },
  methods: {
    async reset() {
      for (var i in resetData) {
        if (resetData.hasOwnProperty(i)) {
          if (typeof resetData[i] != 'object') {
            viewData[i] = resetData[i];
          }
        }
      }

      // Get the project details
      this.activeRequest = this.$t('OrderDetails.loading');

      try {
        const {data} = await this.$http.get(this.$root.url + 'order/' + this.orderId);
        this.activeRequest = '';
        this.order = data.result;
      } catch (res) {
        console.log(res);

        if (typeof res.code != 'undefined' && res.code === 401) {
          return;
        }

        this.activeRequest = '';
        this.alertTitle = this.$t('OrderDetails.errorCouldNotLoad');
        this.alertCritical = true;
        if (typeof res.data.error != 'undefined') {
          this.alertText = res.data.error;
        }

        return;
      }

      this.transactionnum = this.order.transactionnum;
      if (this.transactionnum) {
        this.$parent.viewTitle = this.$t('OrderDetails.title') + ': ' + this.transactionnum;
      }
      for (let i in viewData) {
        if (viewData.hasOwnProperty(i) && this.order.hasOwnProperty(i)) {
          viewData[i] = this.order[i];
        }
      }

      const {extdata} = this.order;
      if (typeof extdata == 'object' && typeof extdata.error == 'string' && extdata.error) {
        this.alertTitle = this.$t('Common.error');
        this.alertCritical = true;
        this.alertText = extdata.error;
        return
      }

      const {data: orderHistory} = await this.$http.get(this.$root.url + 'project?orderby=-creationdate&orderid=' + this.orderId);

      this.orderHistory = [...orderHistory.result.data];
      console.log('viewData', viewData, this);

      this.$forceUpdate();
      console.log('HERE!!!');
    },
    openMessages() {
      this.$refs.messages.openDialog();
    },
    openProject(ID) {
      if (typeof this.project.ID !== 'undefined' || typeof ID !== 'undefined') {
        this.$parent.openView('ProjectDetails', ID ? ID : this.project.ID);
      }
    },
    printUrl(order) {
      return this.$root.url + 'transaction/' + order.ID + '/print/' + order.transactionnum + '.pdf';
    },
    getIconColor(file) {
      return util.fileIconColor(this.getFileExtension(file));
    },
    getDownloadUrl(file) {
      return this.$root.url + 'order/' + this.orderId + '/' + file.binfile + '/' + file.name;
    },
    getFileExtension(file) {
      let ext = file.name.substr(file.name.lastIndexOf('.') + 1);
      return file.name === ext ? null : ext;
    }
  }
});
</script>
<style>
  .order-history {
    border-left: 1px solid grey;
    padding-left: 30px;
    margin: 15px 15px 15px 30px;
  }

  .order-history .order-title {
    font-weight: bold;
    cursor: pointer;
  }

  .order-history .order-date {
    font-size: 80%;
  }

  .order-history > div {
    position: relative;
    margin-bottom: 15px;
    top: -10px;
  }

  .order-history > div::before {
    content: ' ';
    position: absolute;
    top: 10px;
    left: -30px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: red;
    transform: translate(-50%, -50%);
  }
</style>
